/**
 * Bar chart component for comparative analysis
 */

import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import BaseChart from './BaseChart';
import { ChartTheme, getChartColors } from '../../utils/chartConfig';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface BarChartProps {
  data: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor?: string | string[];
      borderColor?: string | string[];
      borderWidth?: number;
      borderRadius?: number;
    }>;
  };
  theme: ChartTheme;
  loading?: boolean;
  error?: string | null;
  height?: number;
  className?: string;
  title?: string;
  showLegend?: boolean;
  showGrid?: boolean;
  horizontal?: boolean;
  stacked?: boolean;
  borderRadius?: number;
}

const BarChart: React.FC<BarChartProps> = ({
  data,
  theme,
  loading = false,
  error = null,
  height = 300,
  className = '',
  title,
  showLegend = true,
  showGrid = true,
  horizontal = false,
  stacked = false,
  borderRadius = 4
}) => {
  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' as const : 'x' as const,
    plugins: {
      title: title ? {
        display: true,
        text: title,
        color: theme.text,
        font: {
          family: 'Inter, system-ui, sans-serif',
          size: 16,
          weight: 'bold' as const
        }
      } : undefined,
      legend: {
        display: showLegend,
        labels: {
          color: theme.text,
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: theme.background,
        titleColor: theme.text,
        bodyColor: theme.textSecondary,
        borderColor: theme.border,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        titleFont: {
          family: 'Inter, system-ui, sans-serif',
          size: 13,
          weight: 'bold' as const
        },
        bodyFont: {
          family: 'Inter, system-ui, sans-serif',
          size: 12
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: showGrid,
          color: theme.grid,
          drawOnChartArea: false
        },
        ticks: {
          color: theme.textSecondary,
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 11
          }
        },
        stacked: stacked
      },
      y: {
        grid: {
          display: showGrid,
          color: theme.grid,
          drawOnChartArea: false
        },
        ticks: {
          color: theme.textSecondary,
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 11
          }
        },
        stacked: stacked
      }
    },
    elements: {
      bar: {
        borderRadius: borderRadius,
        borderSkipped: false
      }
    }
  };

  // Process data to add default colors if not provided
  const processedData = {
    ...data,
    datasets: data.datasets.map((dataset, index) => ({
      ...dataset,
      backgroundColor: dataset.backgroundColor || getChartColors(theme, data.labels.length),
      borderColor: dataset.borderColor || getChartColors(theme, data.labels.length),
      borderWidth: dataset.borderWidth || 1
    }))
  };

  return (
    <BaseChart
      data={processedData}
      options={options}
      theme={theme}
      loading={loading}
      error={error}
      height={height}
      className={className}
    >
      {({ data, options, height }) => (
        <Bar data={data} options={options} />
      )}
    </BaseChart>
  );
};

export default BarChart;
